import { Button } from '@/components/ui/button';
import { Dialog, Transition } from '@headlessui/react';
import { ExternalLinkIcon, PlayIcon, XIcon } from 'lucide-react';
import { Fragment, useState } from 'react';

import { cn } from '../../lib/utils';
import { convertSlackMarkdownToHtml } from '../../utils/slackMarkdownConverter';
import { motion } from 'framer-motion';
import { VoiceWaves } from './voicewaves';
import { useTranslation } from 'react-i18next';

// Session phases
export enum SessionPhase {
  PRE_START = 'pre_start',
  INTRO = 'intro',
  MAIN_CONVERSATION = 'main_conversation',
  ENDING = 'ending',
  DONE = 'done',
}

export default function FrameworkDetailsModal({
  isOpen,
  onClose,
  title,
  content,
  url,
}) {
  const { t } = useTranslation();

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title className="text-xl font-semibold text-gray-900">
                    {title}
                  </Dialog.Title>
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:text-primary/80"
                  >
                    <ExternalLinkIcon className="h-5 w-5" />
                  </a>
                </div>

                <div
                  className="prose prose-sm mt-2 text-gray-600 max-h-[60vh] overflow-y-auto"
                  dangerouslySetInnerHTML={{
                    __html: convertSlackMarkdownToHtml(content),
                  }}
                />

                <div className="mt-6 flex justify-end">
                  <Button onClick={onClose}>
                    {t('coach.framework_modal.close')}
                  </Button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
// ScenarioInfo Component
export function ScenarioInfo({
  scenario,
  framework,
  phase,
  onStart,
  onEnd,
  className,
  ...props
}: {
  scenario: any;
  framework: any;
  phase: SessionPhase;
  onStart: () => void;
  onEnd?: () => void;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  return (
    <div className={cn('space-y-6', className)} {...props}>
      {/* Phase Indicator */}
      <div className="flex items-center space-x-2">
        <div
          className={cn(
            'h-2.5 w-2.5 rounded-full',
            phase === SessionPhase.PRE_START
              ? 'bg-yellow-400'
              : phase === SessionPhase.INTRO
              ? 'bg-blue-400'
              : 'bg-green-400',
          )}
        />
        <span className="text-sm font-medium text-gray-600">
          {phase === SessionPhase.PRE_START
            ? t('coach.scenario.phase.pre_start')
            : phase === SessionPhase.INTRO
            ? t('coach.scenario.phase.intro')
            : t('coach.scenario.phase.main')}
        </span>
      </div>

      {/* Scenario Details */}
      <div className="bg-white rounded-xl p-6 shadow-sm space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            {scenario?.title}
          </h3>
          <p className="mt-2 text-sm text-gray-600">{scenario?.summary}</p>
        </div>

        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-900 mb-2">
              {t('coach.scenario.details.objective')}
            </h4>
            <p className="text-sm text-gray-600">{scenario?.objective}</p>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-900 mb-2">
              {t('coach.scenario.details.framework')}
            </h4>
            <p className="text-sm text-gray-600 font-extrabold">
              {framework?.title || t('coach.scenario.details.no_framework')}
            </p>
            <p className="text-gray-600 text-sm mb-4 mt-2">
              {framework?.description}
            </p>

            <Button
              variant="link"
              size="sm"
              className="text-primary p-0 h-auto"
              onClick={() => setOpen(true)}
            >
              {t('coach.scenario.details.view_details')}
            </Button>
            <FrameworkDetailsModal
              isOpen={open}
              onClose={() => setOpen(false)}
              title={framework?.title}
              content={framework?.detailedGuide}
              url={
                framework?.url ??
                `https://www.google.com/search?q=${framework?.title}`
              }
            />
          </div>
        </div>

        {phase === SessionPhase.PRE_START ? (
          <Button className="w-full" onClick={onStart} size="lg">
            <PlayIcon className="w-4 h-4 mr-2" />
            {t('coach.scenario.buttons.start')}
          </Button>
        ) : onEnd ? (
          <Button className="w-full" onClick={onEnd} size="lg">
            <XIcon className="w-5 h-5 mr-2" />
            {t('coach.scenario.buttons.end')}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

// Role Play Area Component
export const RolePlayArea = ({ isAISpeaking, isMuted, volume, scenario }) => {
  const { t } = useTranslation();

  return (
    <div className="flex-1 flex flex-col">
      {/* Top text section */}
      <div className="w-full p-4">
        <div className="bg-white rounded-lg p-8">
          <h4 className="text-sm font-medium text-gray-900 mb-2">
            {t('coach.roleplay_area.scenario_section.title')}
          </h4>
          <p className="text-sm text-gray-600">{scenario?.introduction}</p>
        </div>
      </div>

      {/* Existing content wrapped in a container to maintain centering */}
      <div className="flex-1 flex items-center justify-center relative">
        <motion.div
          animate={
            isAISpeaking
              ? {
                  scale: [1, 1.05, 1],
                  transition: { duration: 2, repeat: Infinity },
                }
              : {}
          }
        >
          <div className="relative">
            <img
              src="https://dopmo1eihgbgm.cloudfront.net/67135d6f890cc59eb99e65f9/ai-headshot.webp"
              alt="AI Coach"
              className="w-48 h-48 rounded-full shadow-lg"
            />

            {isAISpeaking && (
              <motion.div
                className="absolute inset-0 rounded-full border-4 border-primary"
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.5, 0.2, 0.5],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                }}
              />
            )}

            {/* Voice Wave Visualization */}
            {!isMuted && isAISpeaking && <VoiceWaves volume={volume} />}
          </div>
        </motion.div>
      </div>
    </div>
  );
};
